import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, Plus, X, Pencil, Archive, ArchiveRestore, ArrowRight, Upload, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner, EmptyState, goldBtn, ghostBtn } from "@/pages/_shared";
import { CompanyBadge } from "@/components/CompanyBadge";

const COLORS = ["#D4AF37", "#B08D57", "#8C6D1F", "#3B82F6", "#10B981", "#EF4444", "#A855F7", "#F97316"];
const CURRENCIES = ["GBP", "USD", "EUR", "AUD", "CAD"];

const empty = {
  name: "", legal_name: "", company_type: "Ltd", industry: "general", email: "", phone: "",
  website: "", address: "", currency: "GBP", tax_rate: 20, tax_number: "", color: "#D4AF37",
};

export default function Businesses() {
  const { companies, refreshCompanies, refresh, switchCompany, can } = useOrg();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState(companies || null);
  const [wizard, setWizard] = useState(false);
  const [editing, setEditing] = useState(null);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    const data = await refreshCompanies();
    setList(data);
  }, [refreshCompanies]);

  useEffect(() => { load(); api.get("/onboarding/templates").then(({ data }) => setTemplates(data)).catch(() => {}); }, [load]);
  useEffect(() => { if (params.get("add") === "1") { setWizard(true); setParams({}, { replace: true }); } }, [params, setParams]);

  const open = (c) => { switchCompany(c.id); nav("/app/dashboard"); };
  const archive = async (c) => {
    await api.patch(`/companies/${c.id}`, { status: c.status === "archived" ? "active" : "archived" });
    toast.success(c.status === "archived" ? "Business restored" : "Business archived");
    await load(); await refresh();
  };

  if (list === null) return <Spinner />;
  const active = list.filter((c) => c.status !== "archived");
  const archived = list.filter((c) => c.status === "archived");

  return (
    <Scroller>
      <PageHeader title="Businesses" subtitle="Every company in your group — create, brand and manage them" icon={Layers}
        action={can("settings.manage") && <button onClick={() => setWizard(true)} className={goldBtn} data-testid="add-business-btn"><Plus className="h-4 w-4" /> Add business</button>} />

      {active.length === 0 ? (
        <EmptyState icon={Building2} title="No businesses yet" hint="Add your first company to start managing its customers, jobs, finance and memory.">
          {can("settings.manage") && <button onClick={() => setWizard(true)} className={goldBtn}><Plus className="h-4 w-4" /> Add business</button>}
        </EmptyState>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {active.map((c) => (
            <div key={c.id} className="group flex flex-col rounded-2xl border border-white/8 bg-charcoal p-5 transition-all hover:border-gold/25" data-testid={`business-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <CompanyBadge company={c} size={48} />
                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  {can("settings.manage") && <button onClick={() => setEditing(c)} className="rounded-lg p-1.5 text-white/40 hover:text-white" data-testid={`business-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>}
                  {can("settings.manage") && <button onClick={() => archive(c)} className="rounded-lg p-1.5 text-white/40 hover:text-destructive" data-testid={`business-archive-${c.id}`}><Archive className="h-4 w-4" /></button>}
                </div>
              </div>
              <p className="mt-3 font-heading text-lg font-semibold text-white/90">{c.name}</p>
              <p className="text-xs capitalize text-white/40">{c.company_type ? `${c.company_type} · ` : ""}{c.industry || "business"} · {c.currency}</p>
              <button onClick={() => open(c)} className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-white/10 py-2 text-sm text-white/75 transition-colors hover:border-gold/30 hover:text-white" data-testid={`business-open-${c.id}`}>
                Open <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <>
          <p className="mb-3 mt-8 text-xs uppercase tracking-wider text-white/35">Archived</p>
          <div className="space-y-2">
            {archived.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border border-white/8 bg-charcoal/50 px-4 py-3 opacity-70" data-testid={`business-archived-${c.id}`}>
                <span className="flex items-center gap-3"><CompanyBadge company={c} size={28} /><span className="text-sm text-white/70">{c.name}</span></span>
                {can("settings.manage") && <button onClick={() => archive(c)} className="flex items-center gap-1.5 text-xs text-gold/80 hover:text-gold" data-testid={`business-restore-${c.id}`}><ArchiveRestore className="h-3.5 w-3.5" /> Restore</button>}
              </div>
            ))}
          </div>
        </>
      )}

      {wizard && <BusinessWizard templates={templates} onClose={() => setWizard(false)} onDone={async (c) => { setWizard(false); await load(); await refresh(); toast.success(`${c.name} created`); }} />}
      {editing && <EditBusiness company={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); await refresh(); }} />}
    </Scroller>
  );
}

function Field({ label, children }) {
  return <div><label className="mb-1 block text-xs text-white/50">{label}</label>{children}</div>;
}
const input = "w-full rounded-xl border border-white/10 bg-graphite px-3.5 py-2.5 text-sm focus:border-gold/40 focus:outline-none";

function BusinessWizard({ templates, onClose, onDone }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Business name is required"); return; }
    setSaving(true);
    try {
      const tpl = templates.find((t) => t.id === form.industry);
      const { data } = await api.post("/companies", { ...form, tax_rate: Number(form.tax_rate) || 0, modules: tpl?.modules });
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create business");
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Add a business" onClose={onClose} testid="business-wizard">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Business name *"><input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Black Diamond Elite Cleaning" className={input} data-testid="wiz-name" /></Field>
        <Field label="Legal / trading name"><input value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} className={input} data-testid="wiz-legal" /></Field>
        <Field label="Company type"><input value={form.company_type} onChange={(e) => set("company_type", e.target.value)} placeholder="Ltd, Sole Trader…" className={input} data-testid="wiz-type" /></Field>
        <Field label="Industry">
          <select value={form.industry} onChange={(e) => set("industry", e.target.value)} className={input} data-testid="wiz-industry">
            {(templates.length ? templates : [{ id: "general", label: "General Business" }]).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Contact email"><input value={form.email} onChange={(e) => set("email", e.target.value)} className={input} data-testid="wiz-email" /></Field>
        <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={input} data-testid="wiz-phone" /></Field>
        <Field label="Website"><input value={form.website} onChange={(e) => set("website", e.target.value)} className={input} data-testid="wiz-website" /></Field>
        <Field label="Address"><input value={form.address} onChange={(e) => set("address", e.target.value)} className={input} data-testid="wiz-address" /></Field>
        <Field label="Currency">
          <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className={input} data-testid="wiz-currency">
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Tax rate (%)"><input type="number" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} className={input} data-testid="wiz-tax" /></Field>
      </div>
      <Field label="Brand colour">
        <div className="mt-1 flex flex-wrap gap-2" data-testid="wiz-colors">
          {COLORS.map((c) => (
            <button key={c} onClick={() => set("color", c)} style={{ background: c }} className={`h-7 w-7 rounded-full ring-2 ring-offset-2 ring-offset-charcoal ${form.color === c ? "ring-white" : "ring-transparent"}`} />
          ))}
        </div>
      </Field>
      <p className="mt-2 text-xs text-white/35">You can upload a logo after creating the business. Modules for <span className="text-white/60">{templates.find((t) => t.id === form.industry)?.label || "this industry"}</span> will be set up automatically.</p>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className={ghostBtn}>Cancel</button>
        <button onClick={submit} disabled={saving} className={goldBtn} data-testid="wiz-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create business</button>
      </div>
    </Modal>
  );
}

function EditBusiness({ company, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: company.name || "", legal_name: company.legal_name || "", company_type: company.company_type || "",
    email: company.email || "", phone: company.phone || "", website: company.website || "",
    address: company.address || "", currency: company.currency || "GBP", tax_rate: company.tax_rate ?? 20,
    color: company.color || "#D4AF37",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logo, setLogo] = useState(company.logo_url);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.patch(`/companies/${company.id}`, { ...form, tax_rate: Number(form.tax_rate) || 0 }); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/companies/${company.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data.logo_url); toast.success("Logo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Logo upload failed"); } finally { setUploading(false); }
  };

  return (
    <Modal title={`Edit ${company.name}`} onClose={onClose} testid="business-edit-modal">
      <div className="mb-4 flex items-center gap-4">
        <CompanyBadge company={{ ...company, ...form, logo_url: logo }} size={56} />
        <div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className={ghostBtn} data-testid="logo-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload logo
          </button>
          <p className="mt-1 text-[11px] text-white/35">PNG/JPG, under 3MB</p>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Business name"><input value={form.name} onChange={(e) => set("name", e.target.value)} className={input} data-testid="edit-name" /></Field>
        <Field label="Legal / trading name"><input value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} className={input} /></Field>
        <Field label="Company type"><input value={form.company_type} onChange={(e) => set("company_type", e.target.value)} className={input} /></Field>
        <Field label="Currency">
          <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className={input}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        </Field>
        <Field label="Contact email"><input value={form.email} onChange={(e) => set("email", e.target.value)} className={input} /></Field>
        <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={input} /></Field>
        <Field label="Website"><input value={form.website} onChange={(e) => set("website", e.target.value)} className={input} /></Field>
        <Field label="Tax rate (%)"><input type="number" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} className={input} /></Field>
      </div>
      <Field label="Brand colour">
        <div className="mt-1 flex flex-wrap gap-2">
          {COLORS.map((c) => <button key={c} onClick={() => set("color", c)} style={{ background: c }} className={`h-7 w-7 rounded-full ring-2 ring-offset-2 ring-offset-charcoal ${form.color === c ? "ring-white" : "ring-transparent"}`} />)}
        </div>
      </Field>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className={ghostBtn}>Cancel</button>
        <button onClick={save} disabled={saving} className={goldBtn} data-testid="edit-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save changes</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-white/10 bg-charcoal p-6" data-testid={testid}>
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">{title}</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-white/40" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}
